import { useMemo, useState } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { ActiveTab, useMMMActiveTab } from "@features/mmm/hooks/mmm-url-state";
import {
  type FetchBlessedBudgetMixesForCycle_me_mmm_cycle_budgetMixes,
  type FetchBlessedBudgetMixesForCycle_me_mmm_cycle_clientSettings,
  type FetchMmmSummary_me_mmm_summary_accuracyData_data,
} from "@north-beam/nb-client-side";
import { formatting } from "@north-beam/nb-common";
import { RecapPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import {
  type PartialClientSetting,
  useClientSettingData,
} from "@pages/mmm/hooks/use-client-setting-data";

import { type Kpi } from "../explore-budgets/client-setting/types";
import { AboutThisSettingModal } from "../shared/about-this-setting-modal";

import { formatKpi } from "./summary-accuracy/utils";

const Setting = ({
  mixKpi,
  budgetMix,
  clientSetting,
}: {
  mixKpi?: Kpi;
  budgetMix: FetchBlessedBudgetMixesForCycle_me_mmm_cycle_budgetMixes;
  clientSetting:
    | FetchBlessedBudgetMixesForCycle_me_mmm_cycle_clientSettings
    | undefined;
}) => {
  const { user } = useUser();
  const trackMMMEvent = useTrackMMMEvent(MMMTab.RECAP_PERFORMANCE);
  const { setActiveTab } = useMMMActiveTab();
  const { kpi: adoptedKpi, spendConstraints } = useClientSettingData(
    clientSetting as unknown as PartialClientSetting,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div>
      <span className="font-medium text-xs mb-2 mr-2">SETTING</span>
      <span
        className="text-cosmic text-xs underline cursor-pointer"
        onClick={() => {
          trackMMMEvent({
            component:
              RecapPerformanceTrackedComponent.VIEW_SETTING_IN_EXPLORE_BUDGETS_LINK,
            action: "onClick",
          });

          setActiveTab(ActiveTab.EXPLORE_MIXES);
        }}
      >
        View in &apos;Explore Budgets&apos;
      </span>
      <div
        className={clsx(
          "border rounded-md py-1.5 px-3 w-full cursor-pointer hover:border-interstellar",
        )}
        onClick={() => {
          trackMMMEvent({
            component:
              RecapPerformanceTrackedComponent.ABOUT_THIS_SETTING_MODAL,
            action: "onOpen",
          });

          setIsModalOpen(true);
        }}
      >
        <div className="text-xs font-semibold mb-0.5 flex items-center gap-2">
          <div>
            {budgetMix.client_setting_name || budgetMix.name || "Custom budget"}
          </div>
          {budgetMix.is_custom && (
            <div className="py-0.5 px-3 bg-cosmic-2 rounded-xl text-xxs">
              Custom
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3 pr-8">
          <div className="text-xxs truncate text-ellipsis">
            <span className="text-gray mr-1">KPI:</span>
            <span className="text-cosmic">{mixKpi?.friendly_name}</span>
          </div>
          {!budgetMix.is_custom && clientSetting && (
            <>
              <div className="text-xxs truncate text-ellipsis">
                <span className="text-gray mr-1">Budget:</span>
                <span className="text-cosmic">{`${formatKpi(
                  adoptedKpi?.kpi_type as any,
                  clientSetting.budget_low,
                  user,
                )} - ${formatKpi(
                  adoptedKpi?.kpi_type as any,
                  clientSetting.budget_high,
                  user,
                )}`}</span>
              </div>
              <div className="text-xxs">
                <span className="text-gray mr-1">Constraints:</span>
                <span className="text-cosmic">
                  {spendConstraints?.length ?? 0}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      <AboutThisSettingModal
        setting={{
          name:
            budgetMix.client_setting_name || budgetMix.name || "Custom budget",
          budgetLow: clientSetting?.budget_low,
          budgetHigh: clientSetting?.budget_high,
          kpi: mixKpi
            ? {
                name: mixKpi.friendly_name,
                equation: mixKpi.equation,
              }
            : undefined,
          spendConstraints: (clientSetting?.spend_constraints as any) ?? [],
        }}
        isOpen={isModalOpen}
        onClose={() => {
          trackMMMEvent({
            component:
              RecapPerformanceTrackedComponent.ABOUT_THIS_SETTING_MODAL,
            action: "onClose",
          });

          setIsModalOpen(false);
        }}
      />
    </div>
  );
};

interface BudgetedSpendProps {
  budgetMixSpend: number;
}

const BudgetedSpend = ({ budgetMixSpend }: BudgetedSpendProps) => {
  const { user } = useUser();

  return (
    <div>
      <span className="font-medium text-xs mb-2">
        BUDGETED SPEND <span className="text-xxs">(FROM ADOPTED BUDGET)</span>
      </span>
      <div className="border rounded-md py-1.5 px-3 h-11 flex items-center w-full">
        <div className="font-semibold text-sm">
          {formatting.formatCurrency(budgetMixSpend, {
            code: user.currencies.currentCurrency,
          })}
        </div>
      </div>
    </div>
  );
};

interface ActualSpendProps {
  accuracyData: FetchMmmSummary_me_mmm_summary_accuracyData_data[];
  budgetMixSpend: number;
}

const ActualSpend = ({ accuracyData, budgetMixSpend }: ActualSpendProps) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.RECAP_PERFORMANCE);
  const { user } = useUser();
  const { setActiveTab } = useMMMActiveTab();

  const spendValue = useMemo(() => {
    return accuracyData.reduce((acc, row) => acc + row.spend, 0);
  }, [accuracyData]);

  const budgedAccuracy = 1 - budgetMixSpend / spendValue;

  return (
    <div>
      <span className="font-medium text-xs mb-2 mr-2">ACTUAL SPEND</span>
      <span
        className="text-cosmic text-xs underline cursor-pointer"
        onClick={() => {
          trackMMMEvent({
            component:
              RecapPerformanceTrackedComponent.VIEW_SPEND_IN_PERFORMANCE_TRACKING_LINK,
            action: "onClick",
          });

          setActiveTab(ActiveTab.PERFORMANCE_TRACKING);
        }}
      >
        View in &apos;Performance tracking&apos;
      </span>
      <div className="border rounded-md py-1.5 px-3 h-11 flex items-center space-x-2 w-full">
        <div className="font-semibold text-sm">
          {formatting.formatCurrency(spendValue, {
            code: user.currencies.currentCurrency,
          })}
        </div>
        <div className="py-1 px-2 rounded-full bg-interstellar-3 text-interstellar-1 text-xs">
          {formatting.formatPercentage(Math.abs(budgedAccuracy), {
            precision: "approximate",
          })}{" "}
          {budgedAccuracy > 0 ? "above" : "below"} budgeted
        </div>
      </div>
    </div>
  );
};

interface SummaryInfoProps {
  mixKpi?: Kpi;
  accuracyData: FetchMmmSummary_me_mmm_summary_accuracyData_data[];
  budgetMixSpend: number;
  budgetMix: FetchBlessedBudgetMixesForCycle_me_mmm_cycle_budgetMixes;
  clientSetting:
    | FetchBlessedBudgetMixesForCycle_me_mmm_cycle_clientSettings
    | undefined;
}

export const SummaryInfo = ({
  mixKpi,
  accuracyData,
  budgetMixSpend,
  budgetMix,
  clientSetting,
}: SummaryInfoProps) => {
  return (
    <>
      <div className="text-xl font-bold">About</div>
      <div className="grid grid-cols-3 gap-4">
        <Setting
          budgetMix={budgetMix}
          clientSetting={clientSetting}
          mixKpi={mixKpi}
        />
        <BudgetedSpend budgetMixSpend={budgetMixSpend} />
        <ActualSpend
          accuracyData={accuracyData}
          budgetMixSpend={budgetMixSpend}
        />
      </div>
    </>
  );
};
